import { useCallback, useRef, useState } from 'react'

export function useUnsavedChanges() {
  const [pending, setPending] = useState<{ run: () => void } | null>(null)
  // The tab that went dirty owns the save, and it may not be the one being left.
  const saveRef = useRef<(() => Promise<boolean>) | null>(null)

  const markDirty = useCallback((save: () => Promise<boolean>) => {
    saveRef.current = save
  }, [])

  const markClean = useCallback(() => {
    saveRef.current = null
  }, [])

  const guard = useCallback((run: () => void) => {
    if (saveRef.current) {
      setPending({ run })
    } else {
      run()
    }
  }, [])

  const continuePending = () => {
    markClean()
    pending?.run()
    setPending(null)
  }

  const saveThenContinue = async () => {
    const saved = (await saveRef.current?.()) ?? true
    if (saved) {
      continuePending()
    } else {
      setPending(null)
    }
  }

  const dismiss = () => setPending(null)

  return { pending, markDirty, markClean, guard, saveThenContinue, discardThenContinue: continuePending, dismiss }
}
